import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL } from '../config/canonical';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, CheckCircle2, Eye, EyeOff, ShieldCheck, UserCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Forced first-sign-in screen for applicants who were just promoted into
 * field-investigator User accounts. They land here whenever the /api/auth/login
 * response carries `require_username_change` or `require_password_change`.
 *
 * Step 1 — choose a personal username (anything that isn't NEXUS-XXXX)
 * Step 2 — set a new password (replacing their old portal password)
 *
 * Both steps must be completed before the user can proceed to /dashboard.
 */
const CompleteSetup = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  // Old portal password is required for the password-change endpoint.
  // We capture it once at the top of the flow so the user only types it once.
  const [currentPassword, setCurrentPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);

  // Step-1: username
  const [newUsername, setNewUsername] = useState('');
  // Step-2: password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);

  const [step, setStep] = useState(user?.require_username_change ? 1 : 2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [usernameDone, setUsernameDone] = useState(!user?.require_username_change);
  const [passwordDone, setPasswordDone] = useState(!user?.require_password_change);

  useEffect(() => {
    // If neither flag is set, bounce to dashboard — page should never have been hit.
    if (!user) return;
    if (!user.require_username_change && !user.require_password_change) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const token = () => localStorage.getItem('nexus_token');

  const submitUsername = async (e) => {
    e?.preventDefault?.();
    setError('');
    const u = newUsername.trim();
    if (!u) { setError('Pick a username.'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/change-username`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ new_username: u }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setUser(prev => prev ? { ...prev, username: data.username, require_username_change: false } : prev);
      setUsernameDone(true);
      toast.success('Username set — now create your password');
      setStep(2);
    } catch (err) {
      setError(err.message || 'Could not update username');
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!currentPassword) { setError('Enter your current (applicant portal) password.'); return; }
    if ((newPassword || '').length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (newPassword === currentPassword) { setError('Choose a password that differs from your current one.'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setUser(prev => prev ? { ...prev, require_password_change: false } : prev);
      setPasswordDone(true);
      toast.success('Setup complete — welcome to NexusTrac');
      setTimeout(() => navigate('/dashboard', { replace: true }), 600);
    } catch (err) {
      setError(err.message || 'Could not update password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-[#030812] flex items-center justify-center p-4 py-8"
      data-testid="complete-setup-shell"
    >
      <div className="w-full max-w-md space-y-5 relative z-10">
        <div className="text-center">
          <p className="text-[10px] font-mono uppercase tracking-[0.32em] text-amber-300/90">
            Welcome to NexusTrac
          </p>
          <h1 className="text-2xl font-bold text-white mt-1">Complete your account setup</h1>
          <p className="text-xs text-slate-400 mt-2 leading-relaxed">
            Your applicant account has been converted into a field-investigator account.<br />
            Before you can continue, choose a personal username and set a new password.
          </p>
        </div>

        {/* Progress tracker */}
        <div className="flex items-center gap-3 px-4">
          <StepBadge active={step === 1} done={usernameDone} label="Username" />
          <div className="flex-1 h-px bg-slate-700/70" />
          <StepBadge active={step === 2} done={passwordDone} label="Password" />
        </div>

        <div className="rounded-xl border border-slate-700/60 bg-[#0B1426]/95 p-5 backdrop-blur shadow-2xl">
          {step === 1 && !usernameDone && (
            <form onSubmit={submitUsername} className="space-y-4" data-testid="setup-username-form">
              <div className="flex items-center gap-2 text-amber-200">
                <UserCircle2 className="w-4 h-4" />
                <p className="text-xs font-mono tracking-wider uppercase">Step 1 · Pick your username</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="setup-new-username" className="text-xs text-slate-300">New username</Label>
                <Input
                  id="setup-new-username"
                  data-testid="setup-new-username-input"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="e.g. jane.doe or jdoe"
                  autoFocus
                  className="bg-[#0E1626] border-slate-700 text-white"
                />
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  4–32 characters. Letters, numbers, dots, hyphens and underscores only.
                  Cannot start with <span className="font-mono text-amber-300">NEXUS-</span>.
                </p>
              </div>
              {error && <ErrorBanner msg={error} />}
              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold"
                data-testid="setup-username-submit"
              >
                {busy ? 'Saving…' : 'Continue'}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submitPassword} className="space-y-4" data-testid="setup-password-form">
              <div className="flex items-center gap-2 text-amber-200">
                <ShieldCheck className="w-4 h-4" />
                <p className="text-xs font-mono tracking-wider uppercase">Step 2 · Set your password</p>
              </div>
              <PasswordField
                id="setup-current-password"
                testId="setup-current-password-input"
                label="Current (applicant portal) password"
                value={currentPassword}
                onChange={setCurrentPassword}
                show={showCurrent}
                setShow={setShowCurrent}
                autoFocus
              />
              <PasswordField
                id="setup-new-password"
                testId="setup-new-password-input"
                label="New password"
                value={newPassword}
                onChange={setNewPassword}
                show={showNew}
                setShow={setShowNew}
                hint="Minimum 8 characters."
              />
              <PasswordField
                id="setup-confirm-password"
                testId="setup-confirm-password-input"
                label="Confirm new password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                show={showNew}
                setShow={setShowNew}
              />
              {error && <ErrorBanner msg={error} />}
              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                data-testid="setup-password-submit"
              >
                {busy ? 'Saving…' : 'Finish setup'}
              </Button>
            </form>
          )}
        </div>

        <p className="text-[11px] text-slate-500 text-center">
          Trouble signing in? Contact your administrator.
        </p>
      </div>
    </div>
  );
};

const StepBadge = ({ active, done, label }) => (
  <div className="flex items-center gap-2">
    <div
      className={
        'flex items-center justify-center w-7 h-7 rounded-full border text-xs font-bold ' +
        (done
          ? 'border-emerald-500 bg-emerald-500/20 text-emerald-300'
          : active
            ? 'border-amber-400 bg-amber-400/20 text-amber-200'
            : 'border-slate-600 bg-slate-800 text-slate-500')
      }
    >
      {done ? <CheckCircle2 className="w-4 h-4" /> : label[0]}
    </div>
    <span
      className={
        'text-[11px] font-mono uppercase tracking-wider ' +
        (done ? 'text-emerald-300' : active ? 'text-amber-200' : 'text-slate-500')
      }
    >
      {label}
    </span>
  </div>
);

const PasswordField = ({ id, testId, label, value, onChange, show, setShow, hint, autoFocus }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-xs text-slate-300">{label}</Label>
    <div className="relative">
      <Input
        id={id}
        data-testid={testId}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        className="bg-[#0E1626] border-slate-700 text-white pr-9"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
    {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
  </div>
);

const ErrorBanner = ({ msg }) => (
  <div
    className="flex items-start gap-2 rounded border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-200"
    data-testid="setup-error"
  >
    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
    <span>{msg}</span>
  </div>
);

export default CompleteSetup;
